import "./SaleManager.scss";

import { Box, Grid, isWidthUp, Typography, withWidth } from "@material-ui/core";
import Navbar from 'components/Navbar';
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { PropertiesCompositionLevel } from "../../backend/types/constants";
import { CreateSaleManageable, SaleManageable } from "../../store/sale/types";
import { Mode } from "../../utils/utility";
import { ScreenWidth } from "../../utils/width";
import { ContentWrapper } from "../ContentWrapper";
import { AcceptedFileType } from "../input/elements/FileUpload";
import { SaleField, SaleInputTypeKey } from "../input/fields";
import { SubformProps } from "../SubformProps";
import { FundingProgress } from "./FundingProgress";
import HowItWorks from "./HowItWorks";
import { ImagesManager } from "./ImagesManager";
import { SaleCorePropertiesManager } from "./SaleCorePropertiesManager";
import { SaleMediaPropertyManager } from "./SaleMediaPropertyManager";
import { SaleRichTextPropertiesManager } from "./SaleRichTextPropertiesManager";

export interface SaleManagerProps extends SubformProps<CreateSaleManageable | SaleManageable> {
  mode: Mode;
  onAutocomplete?: () => void;
  onFilesSelected?: (value: Array<AcceptedFileType>) => void;
  onUploadEnded?: (value: Array<AcceptedFileType>) => void;
  width: ScreenWidth;
}

const SaleManager = (props: SaleManagerProps) => {
  // Props
  const { mode } = props;

  const autocompleteProps = { ...props } as any;
  delete autocompleteProps.onFilesSelected;
  delete autocompleteProps.onUploadEnded;

  const uploadProps = { ...props } as any;
  delete uploadProps.onAutocomplete;

  const fieldProps = { ...props } as any;
  delete fieldProps.onAutocomplete;
  delete fieldProps.onFilesSelected;
  delete fieldProps.onUploadEnded;

  // Hooks
  const { t } = useTranslation();

  return (
    <Fragment>
      <Box className="cover">
        <Navbar />

        <ImagesManager
          {...uploadProps}
          id="images"
          className="cover-content"
        />
      </Box>

      <ContentWrapper justifyContent="left" padding="50px 11% 50px 11%">
        <Grid container direction="column" spacing={10}>
          <Grid container direction="row" spacing={4} item>
            <Grid item md={12} lg={true}>
              <Grid container direction="column" spacing={8}>
                {mode === Mode.Edit &&
                <Grid container direction="column" spacing={1} item>
                  <Grid item>
                    <SaleField
                      {...fieldProps}
                      id={"assetType" as SaleInputTypeKey}
                      variant="filled"
                    />
                  </Grid>

                  <Grid item>
                    <SaleField
                      {...fieldProps}
                      id={"investmentClass" as SaleInputTypeKey}
                      variant="filled"
                    />
                  </Grid>

                  <Grid item>
                    <SaleField
                      {...fieldProps}
                      id={"baFinId" as SaleInputTypeKey}
                      variant="filled"
                    />
                  </Grid>
                </Grid>
                }

                <Grid container direction="column" spacing={0} item>
                  <Grid item>
                    <SaleField
                      {...fieldProps}
                      className="sale-supertitle"
                      id={"supertitle" as SaleInputTypeKey}
                      variant="filled"
                    />
                  </Grid>

                  <Grid item>
                    <SaleField
                      {...fieldProps}
                      className="sale-title"
                      id={"title" as SaleInputTypeKey}
                      variant="filled"
                    />
                  </Grid>

                  <Grid item>
                    <SaleField
                      {...fieldProps}
                      className="sale-subtitle"
                      id={"subtitle" as SaleInputTypeKey}
                      variant="filled"
                    />
                  </Grid>

                  {mode === Mode.View &&
                  <Grid container direction="column" spacing={3} item>
                    <Grid item>
                      <Typography className="info-asset-text--funding-progress">
                        {t("Funding progress")}
                      </Typography>
                    </Grid>

                    <Grid item>
                      <FundingProgress {...fieldProps} />
                    </Grid>
                  </Grid>
                  }
                </Grid>

                <Grid container direction="column" spacing={3} item>
                  <Grid item>
                    <Typography className="info-asset-text">
                      {t("Information about the asset")}
                    </Typography>
                  </Grid>

                  <Grid container direction="column" spacing={2} item>
                    <Grid item>
                      <SaleField
                        {...fieldProps}
                        className="sale-description-title"
                        id={"descriptionTitle" as SaleInputTypeKey}
                        variant="filled"
                      />
                    </Grid>

                    <Grid item>
                      <SaleField
                        {...fieldProps}
                        className="sale-description"
                        id={"description" as SaleInputTypeKey}
                        variant="filled"
                      />
                    </Grid>
                  </Grid>

                  <SaleMediaPropertyManager
                    {...fieldProps}
                    id="properties"
                  />
                </Grid>

                <Grid container direction="column" spacing={1} item>
                  <SaleRichTextPropertiesManager
                    {...uploadProps}
                    id="properties"
                    compositionLevel={PropertiesCompositionLevel}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              md={12}
              lg={"auto"}
              className={isWidthUp(
                "lg",
                props.width
              ) ? "core-properties-box" : ""}
            >
              <Grid container direction="column" spacing={4}>
                <SaleCorePropertiesManager
                  {...autocompleteProps}
                  className="core-properties"
                />

                <Grid item>
                  <Typography className="disclaimer">
                    {t("The forecasts are a 15-year average. Forecasts are not a reliable indicator of future performance. The forecast calculations are based on scenarios that the issuer has calculated to the best of its knowledge and belief.")}
                  </Typography>
                </Grid>

                <Grid item>
                  <SaleField
                    {...fieldProps}
                    id={"highlights" as SaleInputTypeKey}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {mode === Mode.View &&
          <Grid item>
            <HowItWorks />
          </Grid>
          }
        </Grid>
      </ContentWrapper>
    </Fragment>
  );
}

SaleManager.defaultProps = {
  mode: Mode.View,
  errors: {},
  touched: {},
  setFieldValue: null,
  setValues: null
}

export default withWidth()(SaleManager);